import styled, { css } from 'styled-components';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import CloseIcon from '@material-ui/icons/Close';
import { DomEvent } from 'leaflet';
import { useMap, useMapEvents } from 'react-leaflet';

import { useTranslation } from 'helpers/hooks';
import { clearPlaces, getPlacesBySearch } from 'store/map/actions';
import { AppState } from 'store/interfaces';
import { LoadingProp } from 'core/settings/styled';

import { debounce } from '../../debounce';
import {
  registerSearchPlaceClearEvent,
  registerSearchPlaceClickEvent,
} from '../../../services/reactGa';

const SearchContainer = styled.div`
  position: absolute;
  top: 10px;
  left: 50%;
  transform: translate(-50%);
  z-index: 1000;
  width: calc(100vw - 20px);
`;

interface InputWrapperProps {
  showBackground: boolean;
}

const InputWrapper = styled.div<InputWrapperProps>`
  position: relative;
  box-sizing: border-box;
  width: calc(100vw - 20px);
  height: 48px;
  padding: 8px 16px;
  line-height: 24px;
  transition-property: width;
  transition-duration: 0.3s;

  display: flex;
  align-items: center;

  ${({ showBackground }) => {
    if (showBackground) {
      return css`
        background: url('assets/icons/icon_search.svg') right 16px top 12px no-repeat #fff;
        background-size: 24px;
      `;
    }

    return css`
      background: #fff;
    `;
  }}

  input {
    flex: 1;
    margin: 0;
    padding: 0;
    font-size: 16px;
    border: 0;
    outline: 0;
    vertical-align: baseline;
    background: transparent;
    list-style: none;
    overflow: visible;
  }

  svg {
    fill: #000;
  }
`;

interface SuggestionsProps {
  showSuggestions: boolean;
}

const Suggestions = styled.div<SuggestionsProps>`
  display: ${({ showSuggestions }) => (showSuggestions ? 'block' : 'none')};
  box-sizing: border-box;
  width: calc(100vw - 20px);
  background: #fff;
  left: 0;
  top: 45px;

  ul {
    padding: 0;
    margin: 0;
    font-size: 16px;
    color: #000;

    li {
      cursor: pointer;
      padding: 5px 10px;
      white-space: nowrap;
      text-overflow: ellipsis;
      overflow: hidden;
    }
  }
`;

export const Search: React.FC = function () {
  const [search, setSearch] = useState<string>('');
  const [showSuggestions, setShowSuggestions] = useState<boolean>(false);
  const { list, isLoading } = useSelector((app: AppState) => app.map.places, shallowEqual);

  const dispatch = useDispatch();
  const ref = useRef();
  const map = useMap();
  const { t } = useTranslation();

  useMapEvents({
    click() {
      if (showSuggestions) {
        setShowSuggestions(false);
      }
    },
  });

  useEffect(() => {
    if (!ref.current) {
      return;
    }

    DomEvent.disableClickPropagation(ref.current);
  }, [ref]);

  const loadPlaces = useCallback(
    debounce((search) => {
      const s = search.trim();
      if (s.length <= 1) {
        dispatch(clearPlaces());
        return;
      }

      dispatch(getPlacesBySearch.request(s));
    }, 500),
    []
  );

  const handleChange = useCallback((e) => {
    setSearch(e.target.value);
    loadPlaces(e.target.value);
  }, []);

  const clearSearch = useCallback(() => {
    registerSearchPlaceClearEvent();
    setSearch('');
    setShowSuggestions(false);
    dispatch(clearPlaces());
  }, []);

  const handlePlaceClick = useCallback((e, place) => {
    registerSearchPlaceClickEvent();
    e.stopPropagation();
    map.flyTo(place.coords);

    if (place.bounds) {
      map.fitBounds(place.bounds);
    }

    setShowSuggestions(false);
  }, []);

  return (
    <SearchContainer
      // @ts-ignore
      ref={ref}
      onBlur={() => {
        setTimeout(() => setShowSuggestions(false), 100);
      }}
    >
      <InputWrapper className="leaflet-touch leaflet-bar" showBackground={search.length === 0}>
        <input
          onFocus={() => setShowSuggestions(true)}
          value={search}
          placeholder={t('map.search')}
          onChange={handleChange}
          type="text"
        />
        {search.length > 0 && <CloseIcon onClick={clearSearch} />}
      </InputWrapper>
      {(list?.length > 0 || isLoading) && (
        <Suggestions className="leaflet-touch leaflet-bar" showSuggestions={showSuggestions}>
          <ul>
            {isLoading ? (
              <li>
                <LoadingProp />
              </li>
            ) : (
              list.map((place) => (
                <li
                  onClick={(e) => handlePlaceClick(e, place)}
                  key={place.coords.toString() + place.name}
                >
                  {place.name}
                </li>
              ))
            )}
          </ul>
        </Suggestions>
      )}
    </SearchContainer>
  );
};
